import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { authorizedFetch } from "@/lib/api";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3001";

interface MutationOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  onRestoreStart?: (id: string) => void;
  onRestoreEnd?: () => void;
}

export function useCargoProductMutations(options: MutationOptions = {}) {
  const qc = useQueryClient();

  const createMut = useMutation({
    mutationFn: async (data: { name: string; code: string; sgtCodes?: string[] | null; cargoTypeId?: string | null }) => {
      const res = await api.cargos.products.$post({ json: data });
      if (!res.ok) throw await res.json();
      return res.json() as Promise<{ code: string; name: string }>;
    },
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["cargos", "products"] });
      toast.success(`Producto creado: ${created.code} — ${created.name}`);
      options.onSuccess?.();
    },
    onError: (err: Error) => {
      toast.error(err.message || "No se pudo crear el producto");
      options.onError?.(err);
    },
  });

  const updateMut = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: { name: string; code: string; sgtCodes?: string[] | null; cargoTypeId?: string | null } }) => {
      const res = await api.cargos.products[":id"].$patch({ param: { id }, json: data });
      if (!res.ok) throw await res.json();
      return res.json() as Promise<{ code: string; name: string }>;
    },
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ["cargos", "products"] });
      toast.success(`Producto actualizado: ${updated.code} — ${updated.name}`);
      options.onSuccess?.();
    },
    onError: (err: Error) => {
      toast.error(err.message || "No se pudo actualizar el producto");
      options.onError?.(err);
    },
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.cargos.products[":id"].$delete({ param: { id } });
      if (!res.ok) throw await res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cargos", "products"] });
      options.onSuccess?.();
    },
    onError: (err: Error) => options.onError?.(err),
  });

  const restoreMut = useMutation({
    mutationFn: async (id: string) => {
      options.onRestoreStart?.(id);
      try {
        const res = await authorizedFetch(`${API_URL}/api/cargos/products/${id}/restore`, {
          method: "PATCH",
        });
        if (!res.ok) throw new Error(res.statusText);
      } finally {
        options.onRestoreEnd?.();
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cargos", "products"] });
      options.onSuccess?.();
    },
    onError: (err: Error) => options.onError?.(err),
  });

  return {
    createMut,
    updateMut,
    deleteMut,
    restoreMut,
    isSaving: createMut.isPending || updateMut.isPending,
  };
}
